import React, { useState, useCallback } from "react";
import { SwipeableDrawer, IconButton, Box } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import clsx from "classnames";
import { PUBLICURL } from "../../main_config";
import menuItems from "../../data/menu_links";
import LinkEcco, { WrappedLinkRouter } from "../Link";
import ButtonSmg from "../ButtonSmg";
import style from "./styles";
import { useStyles } from "../../hooks";
import { useLocation } from "react-router-dom";
import { cotizadorPathFromLocation } from "../../routes/paths";
import { isMenuItemActive } from "../HeaderMenu";
import EmergencyCallDialog from "../EmergencyCallDialog";

const asset = name => `${PUBLICURL}/assets/images/header/${name}`;

const HeaderDrawer = () => {
  const styles = useStyles(style);
  const { pathname } = useLocation();
  const cotizadorTo = cotizadorPathFromLocation(pathname);
  const [open, setOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);

  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const openDrawer = useCallback(() => {
    const match = menuItems.find(
      item => !item.cta && item.static && isMenuItemActive(item, pathname)
    );
    setExpandedKey(match ? match.key : null);
    setOpen(true);
  }, [pathname]);

  const toggleDrawer = useCallback(
    nextOpen => event => {
      if (
        event &&
        event.type === "keydown" &&
        (event.key === "Shift" || event.key === "Tab")
      ) {
        return;
      }
      setOpen(nextOpen);
      if (!nextOpen) {
        setExpandedKey(null);
      }
    },
    []
  );

  const handleToggleSection = useCallback(key => {
    setExpandedKey(prev => (prev === key ? null : key));
  }, []);

  const navItems = menuItems.filter(item => !item.cta);

  return (
    <>
      <Box className={styles.root}>
        <LinkEcco to="/" className={styles.eccoLogo}>
          <img
            src={asset("logo-ecco.png")}
            alt="Ecco"
            className={styles.logo}
            width={103}
            height={34}
          />
        </LinkEcco>
        <Box className={styles.actions}>
          <IconButton
            className={styles.iconBtn}
            onClick={() => setCallDialogOpen(true)}
            aria-label="Llamar a emergencias 0810-333-3226"
          >
            <img
              src={asset("icon-phone.svg")}
              alt=""
              width={24}
              height={24}
              aria-hidden="true"
            />
          </IconButton>
          <IconButton
            className={styles.iconBtn}
            onClick={openDrawer}
            aria-label="Abrir menú"
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      <EmergencyCallDialog
        open={callDialogOpen}
        onClose={() => setCallDialogOpen(false)}
      />

      <SwipeableDrawer
        open={open}
        anchor="right"
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        disableDiscovery
        ModalProps={{ keepMounted: true }}
        PaperProps={{ className: styles.drawerPaper }}
        BackdropProps={{ className: styles.drawerBackdrop }}
      >
        <div className={styles.panel} role="presentation">
          <div className={styles.panelBody}>
            <div className={styles.closeRow}>
              <IconButton
                className={styles.closeBtn}
                onClick={closeDrawer}
                aria-label="Cerrar menú"
              >
                <img
                  src={asset("icon-close-menu.svg")}
                  alt=""
                  width={16}
                  height={16}
                  aria-hidden="true"
                />
              </IconButton>
            </div>

            <nav className={styles.navList} aria-label="Menú principal">
              {navItems.map(item => {
                const isExpandable = Boolean(item.static && item.elemens?.length);
                const isExpanded = expandedKey === item.key;
                const routeActive = isMenuItemActive(item, pathname);
                const showActive = isExpandable ? isExpanded : routeActive;

                if (isExpandable) {
                  return (
                    <div key={item.key} className={styles.section}>
                      <button
                        type="button"
                        className={styles.sectionHeader}
                        aria-expanded={isExpanded}
                        onClick={() => handleToggleSection(item.key)}
                      >
                        <span className={styles.bullet} aria-hidden="true">
                          <img
                            src={asset("icon-menu-bullet.svg")}
                            alt=""
                            width={16}
                            height={16}
                          />
                        </span>
                        <span
                          className={clsx(
                            styles.sectionLabelWrap,
                            showActive && styles.sectionLabelWrapActive
                          )}
                        >
                          <span
                            className={clsx(
                              styles.sectionLabel,
                              showActive && styles.sectionLabelActive
                            )}
                          >
                            {item.label}
                          </span>
                        </span>
                        <img
                          className={clsx(
                            styles.chevron,
                            isExpanded && styles.chevronOpen
                          )}
                          src={asset("icon-chevron-menu.svg")}
                          alt=""
                          width={20}
                          height={20}
                          aria-hidden="true"
                        />
                      </button>

                      {isExpanded && (
                        <div className={styles.submenu}>
                          <div className={styles.submenuRail} aria-hidden="true">
                            <span className={styles.submenuRailLine} />
                          </div>
                          <div className={styles.submenuItems}>
                            {item.elemens.map((child, index) => (
                              <LinkEcco
                                key={child.key}
                                to={child.to || `/${child.key}`}
                                className={clsx(
                                  styles.subLink,
                                  index === 0 &&
                                    child.label === "Servicios" &&
                                    styles.subLinkHeading
                                )}
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </LinkEcco>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={item.key} className={styles.section}>
                    <LinkEcco
                      to={item.to || `/${item.key}`}
                      className={clsx(
                        styles.sectionHeader,
                        styles.sectionHeaderLink
                      )}
                      onClick={closeDrawer}
                    >
                      <span className={styles.bullet} aria-hidden="true">
                        <img
                          src={asset("icon-menu-bullet.svg")}
                          alt=""
                          width={16}
                          height={16}
                        />
                      </span>
                      <span
                        className={clsx(
                          styles.sectionLabelWrap,
                          showActive && styles.sectionLabelWrapActive
                        )}
                      >
                        <span
                          className={clsx(
                            styles.sectionLabel,
                            showActive && styles.sectionLabelActive
                          )}
                        >
                          {item.label}
                        </span>
                      </span>
                    </LinkEcco>
                  </div>
                );
              })}
            </nav>
          </div>

          <div className={styles.ctaWrap}>
            <ButtonSmg
              id="btn-drawer-asociate"
              theme="material"
              color="primary"
              content="Asociate hoy"
              className={styles.cta}
              buttonRoot={false}
              component={WrappedLinkRouter}
              to={cotizadorTo}
              onClick={closeDrawer}
            />
          </div>
        </div>
      </SwipeableDrawer>
    </>
  );
};

export default HeaderDrawer;
